import { AdminApi, ApiClient, ApiError, EcomApi } from '@ecom/sdk';
import { useUiStore } from '@/i18n';
import { renew } from './renew';
import { clearSession, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from './session';

/**
 * The admin's API client.
 *
 * Built on the shared SDK rather than a hand-rolled Axios layer, so the admin
 * and the storefront speak to the API through the same typed surface and a
 * contract change breaks both builds at once (ADR-016).
 *
 * On top of the SDK it adds the one thing a browser SPA needs: transparent
 * token refresh. A 401 triggers a single refresh — shared by every request that
 * hit the wall at the same moment — and the original call is retried once.
 */

/** Empty means same-origin, which is what the dev proxy and a one-domain deployment both do. */
export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  return (configured && configured.replace(/\/$/, '')) || window.location.origin;
}

const baseClient = new ApiClient({
  baseUrl: apiBaseUrl(),
  getAccessToken,
  // Bilingual content — product and category names — is resolved server-side
  // from this. Read per request rather than captured at construction, so
  // switching language re-fetches in the new one.
  defaultQuery: () => ({ locale: useUiStore.getState().locale }),
  // Admin data is never shared between staff members; never cache it.
  fetchOptions: { cache: 'no-store' },
});

export const api = new EcomApi(baseClient);

/** The `/api/admin/*` surface — everything the back office actually does. */
export const adminApi = new AdminApi(baseClient);

/** Called when a session cannot be recovered; the shell routes back to sign-in. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/**
 * One refresh at a time.
 *
 * A dashboard fires half a dozen requests at once; without this they would all
 * refresh in parallel, and rotation-with-reuse-detection would read the second
 * one as a stolen token and revoke the whole family — logging the user out for
 * doing nothing wrong. Other tabs share the stored session too: one renewed a
 * moment ago is waited for (`renew`), not taken for a lost session.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshSession(): Promise<boolean> {
  if (!getRefreshToken()) return false;

  refreshInFlight ??= (async () => {
    try {
      const outcome = await renew({
        stored: getRefreshToken,
        refresh: (token) => api.auth.refresh(token, { token: null }),
        keep: (tokens) => {
          setAccessToken(tokens.accessToken);
          setRefreshToken(tokens.refreshToken);
        },
        isConflict: (error) => error instanceof ApiError && error.code === 'REFRESH_CONFLICT',
        wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      });
      if (outcome === 'lost') clearSession();
      return outcome === 'renewed';
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Runs an API call, refreshing once if the session has expired.
 *
 * Stores wrap every call in this; nothing else needs to think about tokens.
 */
export async function withSession<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;

    if (await refreshSession()) return call();

    // Lost only when nothing is left to renew; a renewal still landing from
    // another tab keeps the session for the next call.
    if (!getRefreshToken()) {
      clearSession();
      onSessionLost?.();
    }
    throw error;
  }
}

/** Turns any thrown value into a message a screen can show. */
export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;

  console.error('[admin] unexpected error:', error);
  return fallback;
}
